import {MacroInputData, MacroInputDefinition} from '@frontapp/workflows/macros';
import {isUndefined, omit} from 'lodash';
import React, {FC, useEffect, useState} from 'react';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {emptyArray} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {MacrosDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/macrosDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {getMacroInputs} from '../../../../../helpers/macroHelpers';
import {MacroModel} from '../../../../../models/api/macroModel';
import {isValidDataFieldsTableValueForType} from '../../../../dataTable/helpers';
import {MacroInputDialog} from './macroInputDialog';

/*
 * Props.
 */

interface MacroInputDialogContainerProps {
  macro: MacroModel;
  onOkClick: (macroInputData: MacroInputData) => void;
  onRequestClose: () => void;
  shouldShowMoveActionWarning: boolean;
  onDismissMoveActionWarning: () => void;
}

/*
 * Component.
 */

export const MacroInputDialogContainer: FC<MacroInputDialogContainerProps> = (props) => {
  const {macro, onRequestClose, onOkClick, shouldShowMoveActionWarning, onDismissMoveActionWarning} = props;

  const macroInputs = getMacroInputs(macro) ?? emptyArray;
  const [macroInputValues, setMacroInputValues] = useState<Partial<MacroInputData>>({});
  const isValid = areMacroInputValuesValid(macroInputValues, macroInputs);

  const {trackDataEvent} = useDataEventTracking();

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, MacrosDataEventObject.MACRO_TEAMMATE_INPUT, {
      macro_id: macro.id,
    });
  }, [macro.id, trackDataEvent]);

  const onSubmit = () => {
    if (!isValid) {
      return;
    }

    trackDataEvent(DataEventActionsEnum.CLICK, MacrosDataEventObject.MACRO_TEAMMATE_INPUT_SUBMIT, {
      macro_id: macro.id,
    });
    onOkClick(macroInputValues);
  };

  return (
    <MacroInputDialog
      macroInputs={macroInputs}
      macroName={macro.name}
      onRequestClose={onRequestClose}
      values={macroInputValues}
      onValuesChange={(inputId, inputValue) => {
        if (!isDefined(inputValue)) {
          setMacroInputValues((prevValues) => omit(prevValues, inputId));
          return;
        }

        setMacroInputValues((prevValues) => ({...prevValues, [inputId]: inputValue}));
      }}
      onSubmit={onSubmit}
      isDisabled={!isValid}
      shouldShowMoveActionWarning={shouldShowMoveActionWarning}
      onDismissMoveActionWarning={onDismissMoveActionWarning}
    />
  );
};

/*
 * Helpers.
 */

export function areMacroInputValuesValid(
  macroInputValues: Partial<MacroInputData>,
  macroInputs: ReadonlyArray<MacroInputDefinition>,
): macroInputValues is MacroInputData {
  return macroInputs.every((macroInput) => {
    const inputValue = macroInputValues[macroInput.id];

    if (isUndefined(inputValue)) {
      return false;
    }

    return isValidDataFieldsTableValueForType(macroInput.type, inputValue);
  });
}
